/**
 * BulkActionsBar Component
 * Toolbar for bulk operations on leads
 */

import { Button } from "@/shared/components/ui";
import { CheckSquare, Square, Download, UserPlus } from "lucide-react";

interface BulkActionsBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBulkReserve: () => void;
  onExportCsv: () => void;
  isAllSelected: boolean;
  availableCredits: number;
  isReserving?: boolean;
}

export function BulkActionsBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
  onBulkReserve,
  onExportCsv,
  isAllSelected,
  availableCredits,
  isReserving = false,
}: BulkActionsBarProps) {
  const canReserve = selectedCount > 0 && availableCredits >= selectedCount;

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center gap-3">
        {/* Select all checkbox */}
        <button
          onClick={isAllSelected ? onDeselectAll : onSelectAll}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900"
        >
          {isAllSelected ? (
            <CheckSquare className="w-5 h-5 text-primary-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
          {isAllSelected ? "Odznacz wszystkie" : `Zaznacz wszystkie (${totalCount})`}
        </button>

        {/* Selection count */}
        {selectedCount > 0 && (
          <span className="text-sm text-gray-500 border-l border-gray-300 pl-3">
            Zaznaczono: <span className="font-medium text-gray-900">{selectedCount}</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Export CSV */}
        <Button
          variant="outline"
          size="sm"
          onClick={onExportCsv}
          disabled={selectedCount === 0}
          className="flex items-center gap-1.5"
        >
          <Download className="w-4 h-4" />
          Eksportuj CSV
        </Button>

        {/* Bulk reserve */}
        <Button
          variant="primary"
          size="sm"
          onClick={onBulkReserve}
          disabled={!canReserve || isReserving}
          isLoading={isReserving}
          className="flex items-center gap-1.5"
        >
          <UserPlus className="w-4 h-4" />
          Zarezerwuj ({selectedCount})
          {selectedCount > 0 && (
            <span className="text-xs opacity-75">
              = {selectedCount} kredyt{selectedCount === 1 ? "" : selectedCount < 5 ? "y" : "ów"}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Export leads to CSV
 */
export function exportLeadsToCsv(
  leads: Array<{
    name?: string;
    phone?: string;
    creditType?: string;
    amount?: number;
    message?: string;
    createdAt?: string;
  }>,
  filename = "leady"
) {
  // CSV headers
  const headers = ["Imię i nazwisko", "Telefon", "Typ kredytu", "Kwota", "Wiadomość", "Data utworzenia"];

  // Map credit types
  const creditTypeLabels: Record<string, string> = {
    hipoteczny: "Hipoteczny",
    gotowkowy: "Gotówkowy",
    konsolidacyjny: "Konsolidacyjny",
  };

  // CSV rows
  const rows = leads.map((lead) => [
    lead.name || "",
    lead.phone || "",
    creditTypeLabels[lead.creditType || ""] || lead.creditType || "",
    lead.amount?.toString() || "",
    (lead.message || "").replace(/"/g, '""'), // Escape quotes
    lead.createdAt ? new Date(lead.createdAt).toLocaleString("pl-PL") : "",
  ]);

  // Build CSV content
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((cell) => `"${cell}"`).join(",")
    ),
  ].join("\n");

  // Add BOM for Excel compatibility with Polish characters
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

  // Create download link
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}_${new Date().toISOString().split("T")[0]}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
